import { Link, useNavigate } from "react-router-dom";

import { useTranslations } from "@/lib/useTranslations";
import {
  LIBRARY_PAGE_SIZE_OPTIONS,
  type LibraryPageSize,
} from "@/core/services/types/library-pagination.types";

interface LibraryPaginationProps {
  page: number;
  pageSize: LibraryPageSize;
  total: number;
  buildHref: (page: number, pageSize?: LibraryPageSize) => string;
}

type PageRangeItem = number | "ellipsis";

const pageSizeSelectClassName =
  "rounded-xl border border-black/10 bg-white px-2.5 py-1.5 text-sm text-whart-text outline-none focus:border-zinc-900";

const navButtonClassName =
  "rounded-xl border border-black/10 bg-white px-3 py-1.5 text-sm text-whart-text transition-colors hover:bg-zinc-50";

const navButtonDisabledClassName =
  "rounded-xl border border-black/10 px-3 py-1.5 text-sm text-neutral-400";

const pageNumberClassName =
  "inline-flex min-w-9 items-center justify-center rounded-xl border border-black/10 bg-white px-2.5 py-1.5 text-sm text-whart-text transition-colors hover:bg-zinc-50";

const pageNumberActiveClassName =
  "inline-flex min-w-9 items-center justify-center rounded-xl border border-black/10 bg-yellow-100 px-2.5 py-1.5 text-sm font-medium text-zinc-900";

const ellipsisClassName =
  "inline-flex min-w-9 items-center justify-center px-1 py-1.5 text-sm text-neutral-400";

const VISIBLE_PAGE_THRESHOLD = 7;
const SIBLING_COUNT = 1;

function buildPageRange(currentPage: number, totalPages: number): PageRangeItem[] {
  if (totalPages <= VISIBLE_PAGE_THRESHOLD) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages]);

  for (
    let pageNumber = currentPage - SIBLING_COUNT;
    pageNumber <= currentPage + SIBLING_COUNT;
    pageNumber += 1
  ) {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      pages.add(pageNumber);
    }
  }

  const sortedPages = [...pages].sort((left, right) => left - right);
  const range: PageRangeItem[] = [];
  let previousPage = 0;

  for (const pageNumber of sortedPages) {
    if (previousPage > 0 && pageNumber - previousPage > 1) {
      range.push("ellipsis");
    }

    range.push(pageNumber);
    previousPage = pageNumber;
  }

  return range;
}

export function LibraryPagination({
  page,
  pageSize,
  total,
  buildHref,
}: LibraryPaginationProps) {
  const t = useTranslations("common.pagination");
  const navigate = useNavigate();

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasPrevious = page > 1;
  const hasNext = page < totalPages;
  const pageRange = buildPageRange(page, totalPages);

  if (total === 0) {
    return null;
  }

  function handlePageSizeChange(nextPageSize: LibraryPageSize) {
    navigate(buildHref(1, nextPageSize));
  }

  return (
    <div className="flex flex-col gap-4 border-t border-black/10 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-center gap-2 text-sm text-neutral-600">
        <label htmlFor="library-page-size" className="font-medium text-whart-text">
          {t("pageSize")}
        </label>
        <select
          id="library-page-size"
          value={pageSize}
          onChange={(event) =>
            handlePageSizeChange(Number(event.target.value) as LibraryPageSize)
          }
          className={pageSizeSelectClassName}
        >
          {LIBRARY_PAGE_SIZE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <p className="text-sm text-neutral-600">
        {t("page", { page, totalPages })}
      </p>

      {totalPages > 1 ? (
        <nav aria-label={t("navigation")} className="flex flex-wrap items-center gap-1.5">
          {hasPrevious ? (
            <Link
              to={buildHref(page - 1, pageSize)}
              className={navButtonClassName}
              aria-label={t("previous")}
            >
              {t("previous")}
            </Link>
          ) : (
            <span className={navButtonDisabledClassName} aria-hidden>
              {t("previous")}
            </span>
          )}

          {pageRange.map((item, index) => {
            if (item === "ellipsis") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className={ellipsisClassName}
                  aria-hidden
                >
                  …
                </span>
              );
            }

            const isActive = item === page;

            if (isActive) {
              return (
                <span
                  key={item}
                  className={pageNumberActiveClassName}
                  aria-current="page"
                >
                  {item}
                </span>
              );
            }

            return (
              <Link
                key={item}
                to={buildHref(item, pageSize)}
                className={pageNumberClassName}
                aria-label={t("goToPageNumber", { page: item })}
              >
                {item}
              </Link>
            );
          })}

          {hasNext ? (
            <Link
              to={buildHref(page + 1, pageSize)}
              className={navButtonClassName}
              aria-label={t("next")}
            >
              {t("next")}
            </Link>
          ) : (
            <span className={navButtonDisabledClassName} aria-hidden>
              {t("next")}
            </span>
          )}
        </nav>
      ) : null}
    </div>
  );
}
